import {
  useCallback,
  useEffect,
  useState,
  type ChangeEvent,
  type FormEvent,
} from "react";
import type { Value as PhoneValue } from "react-phone-number-input";

export interface AuthFormData {
  email: string;
  password: string;
  mobile?: string;
}

export type AuthFormMessage = { type: "success" | "error"; text: string } | null;

type UseAuthFormArgs = {
  onSubmit: (data: AuthFormData) => void;
  message: AuthFormMessage;
};

const INITIAL_FORM_DATA: AuthFormData = {
  email: "",
  password: "",
  mobile: "",
};

const SPECIAL_CHAR_RE = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/;

/** Live password feedback for the signup form (length hint is handled in the view). */
export function getPasswordFeedback(password: string): {
  error: string | null;
  success: boolean;
} {
  if (password.length < 8) {
    return { error: null, success: false };
  }
  if (!SPECIAL_CHAR_RE.test(password)) {
    return { error: "Password must contain a special character.", success: false };
  }
  if (!/[a-z]/.test(password)) {
    return { error: "Password must contain a lowercase letter.", success: false };
  }
  if (!/[A-Z]/.test(password)) {
    return { error: "Password must contain an uppercase letter.", success: false };
  }
  if (!/[0-9]/.test(password)) {
    return { error: "Password must contain a number.", success: false };
  }
  return { error: null, success: true };
}

export function useAuthForm({ onSubmit, message }: UseAuthFormArgs) {
  const [formData, setFormData] = useState<AuthFormData>(INITIAL_FORM_DATA);
  const [showPassword, setShowPassword] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [alertVisible, setAlertVisible] = useState(false);

  useEffect(() => {
    if (!message) return;

    setAlertVisible(true);
    const timeoutId = setTimeout(() => setAlertVisible(false), 4000);
    return () => clearTimeout(timeoutId);
  }, [message]);

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));

      if (name === "password") {
        const feedback = getPasswordFeedback(value);
        setPasswordError(feedback.error);
        setPasswordSuccess(feedback.success);
      }
    },
    []
  );

  const handleMobileChange = useCallback((value?: PhoneValue) => {
    setFormData((prev) => ({
      ...prev,
      mobile: value ?? "",
    }));
  }, []);

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      if (!termsAccepted) {
        setShowTermsModal(true);
        return;
      }
      onSubmit(formData);
    },
    [formData, onSubmit, termsAccepted]
  );

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const toggleTermsAccepted = useCallback(() => {
    setTermsAccepted((prev) => !prev);
  }, []);

  const acceptTerms = useCallback(() => {
    setTermsAccepted(true);
  }, []);

  const openTermsModal = useCallback(() => {
    setShowTermsModal(true);
  }, []);

  const closeTermsModal = useCallback(() => {
    setShowTermsModal(false);
  }, []);

  const mobileValue = formData.mobile
    ? (formData.mobile as PhoneValue)
    : undefined;

  return {
    formData,
    mobileValue,
    showPassword,
    showTermsModal,
    termsAccepted,
    passwordError,
    passwordSuccess,
    alertVisible,
    handleInputChange,
    handleMobileChange,
    handleSubmit,
    togglePasswordVisibility,
    toggleTermsAccepted,
    acceptTerms,
    openTermsModal,
    closeTermsModal,
  };
}
